'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spinner } from '@tranquility-ai/theme-components/spinner';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { Breadcrumbs } from '@tranquility-ai/theme-components/breadcrumbs';
import {
  EmailArchiveViewer,
  type EmailArchiveAttachment,
  type EmailArchiveMessage,
  type EmailArchiveSection,
  type EmailArchiveTreeNode,
  type EmailArchiveViewerProps,
} from '@tranquility-ai/theme-components/email-archive-viewer';

import '@tranquility-ai/theme-components/spinner.css';
import { MediaCard } from '@tranquility-ai/theme-components/media-card';
import {
  JunkdrawerApiError,
  fetchJunkdrawerEmailDetail,
  fetchJunkdrawerEmailList,
  junkdrawerKeys,
  type JunkdrawerEmailAttachment,
  type JunkdrawerEmailListResponse,
} from '@/shared/cors-junkdrawer-api';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import { EmailAttachmentInline } from './EmailAttachmentInline';
import { EmptyState } from './components/EmptyState';
import { formatBytes } from './utils';
import {
  EMAIL_ARCHIVE_PAGE_SIZE,
  MAILBOX_NODE_ID,
  buildEmailArchiveData,
  junkdrawerAttachmentFromThemeItem,
  listPageHasMore,
  mapJunkdrawerSummaryToMessage,
  mergeMessageDetail,
  messageIdForIndex,
} from './email-archive-adapter';
import { writeEmailIndexToUrl } from './email-index-url';

/**
 * Host for theme ``EmailArchiveViewer`` behind ``mediaPlayerTransition.emailArchive``.
 *
 * Owns junkdrawer paging (``onLoadMore`` / ``hasMore`` / ``isLoadingMore``)
 * and renders an opened attachment inside the viewer through
 * ``EmailAttachmentInline`` / MediaDisplay. Outlook's ``AttachmentStrip``
 * still uses ``AttachmentPreviewModal`` when the theme viewer flag is off.
 */
export function ThemeEmailArchiveBody({
  s3Key,
  initialEmailIndex = null,
  syncUrl = false,
  section: sectionProp,
  onSectionChange,
  sectionControl = 'body',
}: {
  s3Key: string;
  initialEmailIndex?: number | null;
  /** Write ``?emailIndex`` on the standalone page. Off inside the in-app modal. */
  syncUrl?: boolean;
  section?: EmailArchiveSection;
  onSectionChange?: (section: EmailArchiveSection) => void;
  sectionControl?: 'body' | 'external';
}) {
  const [internalSection, setInternalSection] = useState<EmailArchiveSection>('messages');
  const section = sectionProp ?? internalSection;
  const handleSectionChange = useCallback(
    (next: EmailArchiveSection) => {
      if (sectionProp == null) setInternalSection(next);
      onSectionChange?.(next);
    },
    [onSectionChange, sectionProp],
  );
  const t = useTimePilotT();
  const { cellId, accessToken, enabled } = useCellServiceSession();
  const sessionReady = enabled && !!s3Key;
  const pagingLockRef = useRef(false);
  const wroteIndexRef = useRef<number | null | undefined>(undefined);

  const [offset, setOffset] = useState(0);
  const [listMeta, setListMeta] = useState<JunkdrawerEmailListResponse | null>(null);
  const [messages, setMessages] = useState<EmailArchiveMessage[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [listError, setListError] = useState<Error | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [selectedMessageId, setSelectedMessageId] = useState<string | undefined>(() =>
    initialEmailIndex != null ? messageIdForIndex(initialEmailIndex) : undefined,
  );
  const [showLandedMessage, setShowLandedMessage] = useState(initialEmailIndex != null);
  const [landedAttachment, setLandedAttachment] =
    useState<JunkdrawerEmailAttachment | null>(null);

  // Reset accumulated pages when the mailbox key changes.
  useEffect(() => {
    pagingLockRef.current = false;
    setOffset(0);
    setListMeta(null);
    setMessages([]);
    setHasMore(false);
    setIsLoadingMore(false);
    setListError(null);
    setInitialLoading(true);
    setSelectedMessageId(
      initialEmailIndex != null ? messageIdForIndex(initialEmailIndex) : undefined,
    );
    setShowLandedMessage(initialEmailIndex != null);
    setLandedAttachment(null);
  }, [s3Key]);

  // Honor a pasted or browser-driven ``emailIndex`` without treating our own
  // ``replaceState`` as a new deep link (that would reload the list).
  useEffect(() => {
    if (wroteIndexRef.current === initialEmailIndex) {
      wroteIndexRef.current = undefined;
      return;
    }
    setSelectedMessageId(
      initialEmailIndex != null ? messageIdForIndex(initialEmailIndex) : undefined,
    );
    setShowLandedMessage(initialEmailIndex != null);
    setLandedAttachment(null);
  }, [initialEmailIndex]);

  useEffect(() => {
    if (!sessionReady || !cellId || !accessToken) return;

    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      if (offset === 0) {
        setInitialLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setListError(null);

      try {
        const page = await fetchJunkdrawerEmailList({
          cellId,
          accessToken,
          s3Url: s3Key,
          offset,
          limit: EMAIL_ARCHIVE_PAGE_SIZE,
          sortDesc: true,
          signal: controller.signal,
        });
        if (cancelled) return;

        const mapped = page.emails.map(mapJunkdrawerSummaryToMessage);
        setListMeta(page);
        setMessages((prev) => (offset === 0 ? mapped : [...prev, ...mapped]));
        setHasMore(listPageHasMore(page));
      } catch (err) {
        if (cancelled || controller.signal.aborted) return;
        setListError(err instanceof Error ? err : new Error('Failed to load mailbox'));
        if (offset === 0) {
          setMessages([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) {
          pagingLockRef.current = false;
          setInitialLoading(false);
          setIsLoadingMore(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [sessionReady, cellId, accessToken, s3Key, offset]);

  const selectedEmailIndex = useMemo(() => {
    if (!selectedMessageId) return null;
    const match = selectedMessageId.match(/^msg-(\d+)$/);
    return match ? Number(match[1]) : null;
  }, [selectedMessageId]);

  const detailQuery = useQuery({
    queryKey: junkdrawerKeys.emailDetail(cellId, s3Key, selectedEmailIndex ?? -1),
    queryFn: ({ signal }) =>
      fetchJunkdrawerEmailDetail({
        cellId: cellId as string,
        accessToken: accessToken as string,
        s3Url: s3Key,
        emailIndex: selectedEmailIndex as number,
        signal,
      }),
    enabled: sessionReady && selectedEmailIndex != null,
    retry: (failureCount, err) => {
      if (err instanceof JunkdrawerApiError && err.status === 404) return false;
      return failureCount < 3;
    },
  });

  useEffect(() => {
    if (!detailQuery.data) return;
    setMessages((prev) => mergeMessageDetail(prev, detailQuery.data));
  }, [detailQuery.data]);

  useEffect(() => {
    if (showLandedMessage && detailQuery.isError) setShowLandedMessage(false);
  }, [showLandedMessage, detailQuery.isError]);

  const data = useMemo(
    () =>
      buildEmailArchiveData({
        s3Key,
        list: listMeta,
        messages,
      }),
    [s3Key, listMeta, messages],
  );

  const handleLoadMore = useCallback(() => {
    if (!hasMore || isLoadingMore || initialLoading || pagingLockRef.current) return;
    pagingLockRef.current = true;
    setIsLoadingMore(true);
    setOffset((prev) => prev + EMAIL_ARCHIVE_PAGE_SIZE);
  }, [hasMore, isLoadingMore, initialLoading]);

  const writeSelection = useCallback((message: EmailArchiveMessage | undefined) => {
    setSelectedMessageId(message?.id);
    const match = message?.id.match(/^msg-(\d+)$/);
    const index = match ? Number(match[1]) : null;
    wroteIndexRef.current = index;
    if (syncUrl) writeEmailIndexToUrl(index);
  }, [syncUrl]);

  const handleMessageSelect = useCallback(
    (message: EmailArchiveMessage | undefined) => {
      setShowLandedMessage(false);
      writeSelection(message);
    },
    [writeSelection],
  );

  const renderAttachment = useCallback(
    (item: EmailArchiveTreeNode | EmailArchiveAttachment) => {
      const attachment = junkdrawerAttachmentFromThemeItem(item);
      if (!attachment) return null;
      return <EmailAttachmentInline attachment={attachment} />;
    },
    [],
  );

  if (!sessionReady || initialLoading) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col items-center justify-center gap-2 text-sm text-gray-500">
        <Spinner size="lg" label={t('viewers.loadingMailbox')} />
        {t('viewers.loadingMailbox')}
      </div>
    );
  }

  if (listError && messages.length === 0) {
    const notIngested =
      listError instanceof JunkdrawerApiError && listError.status === 404;
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center bg-gray-50">
        <EmptyState
          kind={notIngested ? 'not-ingested' : 'error'}
          detail={listError.message}
        />
      </div>
    );
  }

  const viewerProps = {
    data,
    section,
    onSectionChange: handleSectionChange,
    sectionControl,
    defaultExpandedIds: [MAILBOX_NODE_ID],
    onMessageSelect: handleMessageSelect,
    onContactSelect: () => undefined,
    contactStatus: 'idle',
    loading: false,
    renderAttachment,
    onLoadMore: handleLoadMore,
    hasMore,
    isLoadingMore,
    className: 'h-full min-h-0 w-full flex-1',
  } as EmailArchiveViewerProps;

  const landedMessage = showLandedMessage
    ? messages.find((message) => message.id === selectedMessageId)
    : undefined;
  const landedBody = landedMessage?.bodyHtml || landedMessage?.bodyText;
  const landedSubject = landedMessage?.subject || t('viewers.emailBody');

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col bg-white">
      {showLandedMessage && landedMessage && landedBody ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="shrink-0 border-b border-gray-200 px-4 py-3">
            <Breadcrumbs
              tone="light"
              aria-label={t('viewers.breadcrumb')}
              root={{ id: 'messages', label: t('viewers.messages') }}
              items={
                landedAttachment
                  ? [
                      { id: 'message', label: landedSubject },
                      { id: 'attachment', label: landedAttachment.fileName },
                    ]
                  : [{ id: 'message', label: landedSubject }]
              }
              onNavigate={(_item, index) => {
                if (index < 0) {
                  setLandedAttachment(null);
                  setShowLandedMessage(false);
                  return;
                }
                if (index === 0) setLandedAttachment(null);
              }}
            />
          </div>
          {landedAttachment ? (
            <EmailAttachmentInline attachment={landedAttachment} />
          ) : (
            <>
              <div className="shrink-0 border-b border-gray-200 px-4 py-3 text-sm font-medium">
                {landedSubject}
              </div>
              {(landedMessage.attachments?.length ?? 0) > 0 ? (
                <ul className="flex shrink-0 flex-wrap gap-4 border-b border-gray-200 px-4 py-3">
                  {landedMessage.attachments!.map((item) => {
                    const meta = [item.mimeType, item.sizeBytes != null ? formatBytes(item.sizeBytes) : '']
                      .filter(Boolean)
                      .join(' · ');
                    return (
                      <li key={item.id}>
                        <MediaCard
                          label={item.filename}
                          meta={meta || undefined}
                          mediaType={item.mediaType}
                          previewSrc={item.thumbnailUrl}
                          tone="light"
                          onSelect={() => {
                            const attachment = junkdrawerAttachmentFromThemeItem(item);
                            if (attachment) setLandedAttachment(attachment);
                          }}
                        />
                      </li>
                    );
                  })}
                </ul>
              ) : null}
              {landedMessage.bodyHtml ? (
                <iframe
                  title={landedSubject}
                  srcDoc={landedMessage.bodyHtml}
                  sandbox=""
                  referrerPolicy="no-referrer"
                  className="min-h-0 w-full flex-1 border-0"
                />
              ) : (
                <pre className="min-h-0 flex-1 overflow-auto whitespace-pre-wrap px-4 py-3 text-sm">
                  {landedMessage.bodyText}
                </pre>
              )}
            </>
          )}
        </div>
      ) : (
        <EmailArchiveViewer {...viewerProps} />
      )}
    </div>
  );
}

/** Full-viewport host used before the shell. Prefer ``ThemeEmailArchiveShell``. */
export function ThemeEmailArchiveViewer({
  s3Key,
  initialEmailIndex = null,
}: {
  s3Key: string;
  initialEmailIndex?: number | null;
}) {
  return (
    <div className="h-screen w-full">
      <ThemeEmailArchiveBody s3Key={s3Key} initialEmailIndex={initialEmailIndex} syncUrl />
    </div>
  );
}
